type X509 = typeof import("@peculiar/x509");

let x509Promise: Promise<X509> | undefined;

function loadX509(): Promise<X509> {
  return (x509Promise ??= import("reflect-metadata").then(
    () => import("@peculiar/x509")
  ));
}

export async function parseX509Certificate(
  pem: string
): Promise<{ rawData: ArrayBuffer; notAfter: Date }> {
  const x509 = await loadX509();
  return new x509.X509Certificate(pem);
}

const TEN_YEARS_MS = 3650 * 24 * 60 * 60 * 1000;
// Backdated so leaves signed by a freshly-minted CA (where the leaf issuer
// backdates a few seconds for clock skew) still satisfy
// `leaf.notBefore >= ca.notBefore`.
const NOT_BEFORE_BACKDATE_MS = 60 * 60 * 1000;
const EXT_KEY_USAGE_CLIENT_AUTH = "1.3.6.1.5.5.7.3.2";

function notBefore(): Date {
  return new Date(Date.now() - NOT_BEFORE_BACKDATE_MS);
}

async function generateP256Keys(): Promise<CryptoKeyPair> {
  return (await crypto.subtle.generateKey(
    { name: "ECDSA", namedCurve: "P-256" },
    true,
    ["sign", "verify"]
  )) as CryptoKeyPair;
}

async function toPkcs8Pem(x509: X509, key: CryptoKey): Promise<string> {
  const der = await crypto.subtle.exportKey("pkcs8", key);
  return x509.PemConverter.encode(der, "PRIVATE KEY");
}

function randomSerial(): string {
  return crypto.randomUUID().replace(/-/g, "");
}

function bytesToHex(bytes: ArrayBuffer): string {
  return Array.from(new Uint8Array(bytes), (b) =>
    b.toString(16).padStart(2, "0")
  ).join("");
}

/**
 * Self-signed P-256 ECDSA client cert + key (PEM). Tony pins the SHA-256 of
 * the leaf DER, same trust shape as SSH host keys.
 */
export async function generateClientCert(commonName: string): Promise<{
  certPem: string;
  keyPem: string;
  sha256Hex: string;
}> {
  const [x509, keys] = await Promise.all([loadX509(), generateP256Keys()]);
  const cert = await x509.X509CertificateGenerator.createSelfSigned({
    serialNumber: randomSerial(),
    name: `CN=${commonName}`,
    notBefore: notBefore(),
    notAfter: new Date(Date.now() + TEN_YEARS_MS),
    signingAlgorithm: { name: "ECDSA", hash: "SHA-256" },
    keys,
    extensions: [
      new x509.KeyUsagesExtension(x509.KeyUsageFlags.digitalSignature, true),
      new x509.ExtendedKeyUsageExtension([EXT_KEY_USAGE_CLIENT_AUTH]),
    ],
  });
  const sha256Hex = bytesToHex(
    await crypto.subtle.digest("SHA-256", cert.rawData)
  );
  return {
    certPem: cert.toString("pem"),
    keyPem: await toPkcs8Pem(x509, keys.privateKey),
    sha256Hex,
  };
}

/**
 * Self-signed P-256 ECDSA CA cert + key (PEM). Used by the dev tunnel gateway
 * to sign its serving leaf; prod ships a long-lived CA in Secrets Manager
 * instead and never calls this.
 */
export async function generateSelfSignedCa(commonName: string): Promise<{
  certPem: string;
  keyPem: string;
}> {
  const [x509, keys] = await Promise.all([loadX509(), generateP256Keys()]);
  const cert = await x509.X509CertificateGenerator.createSelfSigned({
    serialNumber: randomSerial(),
    name: `CN=${commonName}`,
    notBefore: notBefore(),
    notAfter: new Date(Date.now() + TEN_YEARS_MS),
    signingAlgorithm: { name: "ECDSA", hash: "SHA-256" },
    keys,
    extensions: [
      new x509.BasicConstraintsExtension(true, 0, true),
      new x509.KeyUsagesExtension(
        x509.KeyUsageFlags.keyCertSign | x509.KeyUsageFlags.cRLSign,
        true
      ),
    ],
  });
  return {
    certPem: cert.toString("pem"),
    keyPem: await toPkcs8Pem(x509, keys.privateKey),
  };
}
